import * as cheerio from 'cheerio';
import {
  applyNewSpecificDesignRules,
  calculatePartHash,
  resetSpecificDesignRules,
  Template,
  TemplateEditorState,
  updateOpenedTemplate,
} from '@/composables/template-editor/TemplateEditor';
import { additionalStylesConfig } from '@/components/template-builder/config/sync-elements-config';
import {
  ElementPartTypeEnum,
  ResponsiveDisplayStackEnum,
  TemplateStructureEnum,
  WidgetTypeEnum,
} from '@/types';
import {
  generateUniqStructureId,
  getTemplateParentTypeByType,
  getWidgetType,
  isDisplayTemplate,
  replaceTranslationsByKey,
} from './helpers';
import {
  BUILDER_ELEMENT_ACTIVE_CLASS,
  BUILDER_ELEMENT_HOVER_CLASS,
  HIDE_MOBILE_ATTRIBUTE,
  TEMPLATE_PART_ELEMENT_IDENTIFIER,
  TEMPLATE_PART_ELEMENT_PART_TYPE,
  TEMPLATE_PART_ELEMENT_SUBTYPE,
  TEMPLATE_SYNC_ELEMENT_CLASS,
  TEMPLATE_SYNC_ELEMENT_IDENTIFIER,
  TEMPLATE_SYNC_ELEMENT_IDs,
  TEMPLATE_SYNC_ELEMENT_NAME,
} from './constants';

const mergeTranslations = (oldTrans: any, newTrans: any): any => {
  const mergedTrans = { ...oldTrans };

  // eslint-disable-next-line no-restricted-syntax
  for (const lang in newTrans) {
    if (mergedTrans[lang]) {
      mergedTrans[lang] = {
        ...mergedTrans[lang],
        ...newTrans[lang],
      };
    } else {
      mergedTrans[lang] = newTrans[lang];
    }
  }

  return mergedTrans;
};

const applyMappingToTranslations = (
  translations: any,
  mapping: Record<string, string>,
): any => {
  const updatedTranslations: any = {};

  // eslint-disable-next-line no-restricted-syntax, guard-for-in
  for (const lang in translations) {
    updatedTranslations[lang] = {};

    const langData = translations[lang];
    // eslint-disable-next-line no-restricted-syntax, guard-for-in
    for (const oldKey in langData) {
      const newKey = mapping[oldKey] || oldKey;
      updatedTranslations[lang][newKey] = langData[oldKey];
    }
  }

  return updatedTranslations;
};

const updateHtmlWithNewKeys = (
  html: string,
  keyMapping: Record<string, string>,
): string => {
  Object.keys(keyMapping).forEach((oldKey) => {
    const newKey = keyMapping[oldKey];
    const regex = new RegExp(oldKey, 'g');
    // eslint-disable-next-line no-param-reassign
    html = html.replace(regex, newKey);
  });

  return html;
};

const generateNewKeysMapping = (keys: string[]): Record<string, string> => {
  const keyMapping: Record<string, string> = {};

  keys.forEach((key) => {
    const newKey = generateUniqStructureId();
    keyMapping[key] = newKey;
  });

  return keyMapping;
};

const extractKeysFromLanguage = (translations: any): string[] => {
  const allKeys = new Set<string>();
  // eslint-disable-next-line no-restricted-syntax, guard-for-in
  for (const lang in translations) {
    const keys = Object.keys(translations[lang] || {});
    keys.forEach((item) => allKeys.add(item));
  }
  return Array.from(allKeys);
};

const getNewPartTranslations = (newTrans: any, newHtml: string) => {
  const newTranslationsObject = JSON.parse(newTrans);

  const existingKeys = extractKeysFromLanguage(newTranslationsObject);

  const keyMapping = generateNewKeysMapping(existingKeys);

  // eslint-disable-next-line no-param-reassign
  newHtml = updateHtmlWithNewKeys(newHtml, keyMapping);

  const newPartTranslations = applyMappingToTranslations(
    newTranslationsObject,
    keyMapping,
  );

  return {
    newPartTranslations,
    newHtml,
  };
};

const mergeAndConcatCss = (oldObj: any, newObj: any) => {
  const mergedObj = { ...oldObj };

  // eslint-disable-next-line no-restricted-syntax
  for (const key in newObj) {
    if (mergedObj[key]) {
      mergedObj[key] = `${mergedObj[key]} ${newObj[key]}`;
    } else {
      mergedObj[key] = newObj[key];
    }
  }

  return mergedObj;
};

const extractCssRulesForIds = (cssObject: any, idsToKeep: string[]): any => Object.keys(cssObject).reduce((acc, key) => {
  const css = cssObject[key];
  let extractedRules = '';

  // Check if this is a widget-specific key that should be kept
  const isWidgetKey = idsToKeep.some((id) => {
    const refactoId = id.replace('spm_widget_', '');
    return key === `custom_widget_${refactoId}` || key === `custom_widget_${refactoId}_mobile`;
  });

  if (isWidgetKey) {
    acc[key] = css;
    return acc;
  }

  // Extract rules that match any of the IDs to keep
  idsToKeep.forEach((id) => {
    const regex = new RegExp(`([^{}]*#${id}[^{]*{[^}]*})`, 'gm');
    const matches = css.match(regex);
    if (matches) {
      extractedRules += `${matches.join('\n')}\n`;
    }
  });

  // Only add to result if we found matching rules
  if (extractedRules.trim()) {
    acc[key] = extractedRules.trim();
  }

  return acc;
}, {} as Record<string, string>);

const getNewPartCss = (
  idMapping: Record<string, string>,
  newCss: string,
  strict = false,
): any => {
  const newCssObject = JSON.parse(newCss || '{}');
  Object.keys(newCssObject).forEach((category) => {
    if (typeof newCssObject[category] === 'string') {
      Object.entries(idMapping).forEach(([oldId, newId]) => {
        const cssRegex = new RegExp(oldId, 'g');
        newCssObject[category] = newCssObject[category].replace(
          cssRegex,
          newId,
        );
      });
    }
  });

  // Import widget css
  Object.entries(idMapping).forEach(([oldId, newId]) => {
    const replacedOldId = oldId.replace('spm_widget_', '');
    const replacedNewId = newId.replace('spm_widget_', '');
    if (newCssObject[`custom_widget_${replacedOldId}`]) {
      newCssObject[`custom_widget_${replacedNewId}`] = newCssObject[`custom_widget_${replacedOldId}`];
      delete newCssObject[`custom_widget_${replacedOldId}`];
    }
    if (newCssObject[`custom_widget_${replacedOldId}_mobile`]) {
      newCssObject[`custom_widget_${replacedNewId}_mobile`] = newCssObject[`custom_widget_${replacedOldId}_mobile`];
      delete newCssObject[`custom_widget_${replacedOldId}_mobile`];
    }
  });

  // If strict mode is enabled, filter CSS to keep only rules that match the new IDs
  if (strict) {
    const newIds = Object.values(idMapping);
    return extractCssRulesForIds(newCssObject, newIds);
  }

  return newCssObject;
};

const applyIdMappingToHtml = (
  html: string,
  idMapping: Record<string, string>,
): string => Object.keys(idMapping).reduce((acc, oldId) => {
  const regex = new RegExp(`id=["']${oldId}["']`, 'g');
  return acc.replace(regex, `id="${idMapping[oldId]}"`);
}, html);

const generateNewIdMapping = (ids: string[]): Record<string, string> => {
  const idMapping: Record<string, string> = {};

  const regex = /^(spm_(section|widget|row|column))_([a-zA-Z0-9]+)(.*)$/;

  ids.forEach((oldId) => {
    const match = oldId.match(regex);
    if (match) {
      const prefix = match[1]; // Prefix, e.g. "spm_section" or "spm_widget"
      const uniqueKey = match[3]; // Single portion, e.g. "9c480a741348f0fbd54"
      const suffix = match[4] || ''; // Possible suffix, e.g. "_content"

      const newKey = generateUniqStructureId();
      const newId = `${prefix}_${newKey}${suffix}`;

      idMapping[oldId] = newId;
    }
  });
  return idMapping;
};

const getChildElementsIdentifiers = (html: string) => {
  const $newHtml = cheerio.load(html);
  const newChildElements = $newHtml('body').find(
    '[id^="spm_column_"], [id^="spm_row_"], [id^="spm_widget_"], [id^="spm_section_"]',
  );
  const keepChildElementsIdentifiers: string[] = [];
  if (newChildElements.length > 0) {
    newChildElements.each((i, el) => {
      const childElement = $newHtml(el);
      if (
        childElement.attr(TEMPLATE_PART_ELEMENT_IDENTIFIER) &&
        childElement.attr(TEMPLATE_PART_ELEMENT_PART_TYPE)
      ) {
        keepChildElementsIdentifiers.push(
          childElement.attr(TEMPLATE_PART_ELEMENT_IDENTIFIER) || '',
        );
      }
    });
  }
  return keepChildElementsIdentifiers;
};

const replaceOldIdsWithNew = (newHtml: string) => {
  const $newHtml = cheerio.load(newHtml);
  const newChildElements = $newHtml('body').find(
    '[id^="spm_column_"], [id^="spm_row_"], [id^="spm_widget_"], [id^="spm_section_"]',
  );
  const idsToRegenerate = [
    ...newChildElements.map((i, el) => $newHtml(el).attr('id')).get(),
  ];

  const idMapping = generateNewIdMapping(idsToRegenerate);
  return {
    newHtml: applyIdMappingToHtml(newHtml, idMapping),
    idMapping,
  };
};

const updateTranslations = (
  template: any,
  html: string,
  translations: string,
  syncElementId: string,
  elementId: string,
) => {
  const $ = cheerio.load(html);
  const syncElement = $('body').find(
    `[${TEMPLATE_SYNC_ELEMENT_IDENTIFIER}="${syncElementId}"][id="${elementId}"]`,
  );
  const keysToKeep: any = [];
  const translationsToKeep: Record<string, any> = {};

  const translationsObject = JSON.parse(translations);
  const keyRegExp = /\{LANG_([a-zA-Z0-9]+)(?:\.[a-zA-Z0-9]+)?\}/g;

  if (getWidgetType(syncElement.attr('data-widgettype') || '') === WidgetTypeEnum.SMART_PRODUCT_LIST) {
    // Smart list title
    const smartlistTitle = syncElement.find('.spm_product_list_title');
    if (smartlistTitle.length) {
      const titleHtml = smartlistTitle.html() || '';
      let match;

      // eslint-disable-next-line no-cond-assign
      while ((match = keyRegExp.exec(titleHtml)) !== null) {
        const key = match[1];
        keysToKeep.push({
          key,
          type: 'title',
        });
      }
    }

    // Smart list button
    if (template.smartLists[elementId]) {
      const $smartlistBodyHtml = cheerio.load(replaceTranslationsByKey(template.smartLists[elementId], template.translations), {
        decodeEntities: false,
      });
      const smartlistButtonSelector = isDisplayTemplate(template.type) ? 'a.spm_product_list_element_button' : '.spm_product_list_element_button > tbody > tr > th > a';
      const smartlistButton = $smartlistBodyHtml('body').find(smartlistButtonSelector);
      if (smartlistButton.length) {
        const buttonHtml = smartlistButton.html() || '';
        let match;

        // eslint-disable-next-line no-cond-assign
        while ((match = keyRegExp.exec(buttonHtml)) !== null) {
          const key = match[1];
          keysToKeep.push({
            key,
            type: 'button',
          });
        }
      }
    }
  }

  let elementHtml = $.html(syncElement);
  if (template.smartLists[elementId]) {
    elementHtml = replaceTranslationsByKey(template.smartLists[elementId], template.translations);
  }
  const keepTranslations: string[] = [];
  const childElements = syncElement.find(
    '[id^="spm_column_"], [id^="spm_row_"], [id^="spm_widget_"], [id^="spm_section_"]',
  );
  if (childElements.length > 0) {
    childElements.each((i, el) => {
      const childElement = $(el);
      if (childElement.attr(TEMPLATE_PART_ELEMENT_IDENTIFIER) && childElement.attr(TEMPLATE_PART_ELEMENT_PART_TYPE)) {
        const childElementHtml = $.html(childElement);
        let match;
        // eslint-disable-next-line no-cond-assign
        while ((match = keyRegExp.exec(childElementHtml)) !== null) {
          const key = match[1];
          keepTranslations.push(key);
        }
      }
    });
  }

  let match;
  // eslint-disable-next-line no-cond-assign
  while ((match = keyRegExp.exec(elementHtml)) !== null) {
    const key = match[1];

    // eslint-disable-next-line no-restricted-syntax
    for (const lang in translationsObject) {
      if (translationsObject[lang][key]) {
        if (keysToKeep.some((item: any) => item.key === key)) {
          const keyToKeep = keysToKeep.find((item: any) => item.key === key);
          if (keyToKeep && keyToKeep.type) {
            const keyType = keyToKeep.type;
            if (!translationsToKeep[lang]) {
              translationsToKeep[lang] = {};
            }
            translationsToKeep[lang][keyType] = translationsObject[lang][key];
          }
        }
        if (!keepTranslations.includes(key)) {
          delete translationsObject[lang][key];
        }
      }
    }
  }

  return { translationsObject, translationsToKeep };
};

const removeCssRules = (cssObject: any, idsToRemove: string[]): any => Object.keys(cssObject).reduce((acc, key) => {
  let css = cssObject[key];

  const idRemoved = idsToRemove.some((id) => {
    const regex = new RegExp(`([^{}]*#${id}[^{]*{[^}]*})`, 'gm');
    if (regex.test(css)) {
      css = css.replace(regex, '').trim();

      const refactoId = id.replace('spm_widget_', '');

      if (
        key === `custom_widget_${refactoId}`
          || key === `custom_widget_${refactoId}_mobile`
      ) {
        delete acc[`custom_widget_${refactoId}`];
        delete acc[`custom_widget_${refactoId}_mobile`];
        return true;
      }
    }
    return false;
  });

  if (!idRemoved) {
    acc[key] = css;
  }

  return acc;
}, {} as Record<string, string>);

const getElementIdsToRemove = (
  html: string,
  syncElementId: string,
  elementId: string,
  extractSyncElementStylesOnly = false,
): string[] => {
  const $ = cheerio.load(html);
  const syncElement = $('body').find(
    `[${TEMPLATE_SYNC_ELEMENT_IDENTIFIER}="${syncElementId}"][id="${elementId}"]`,
  );
  const childElements = syncElement.find(
    '[id^="spm_column_"], [id^="spm_row_"], [id^="spm_widget_"], [id^="spm_section_"]',
  );
  if (extractSyncElementStylesOnly) {
    return [syncElement.attr('id') || ''];
  }
  return [
    syncElement.attr('id') || '',
    ...childElements.map((i, el) => $(el).attr('id')).get(),
  ];
};

const updateCss = (
  css: string,
  syncElementId: string,
  elementId: string,
  html: string,
  extractSyncElementStylesOnly = false,
) => {
  const idsToRemove = getElementIdsToRemove(
    html,
    syncElementId,
    elementId,
    extractSyncElementStylesOnly,
  );

  const cssObject = JSON.parse(css);
  return removeCssRules(cssObject, idsToRemove);
};

const findSyncElementIds = (syncElementId: string, html: string): string[] => {
  const $ = cheerio.load(html);
  const syncElements = $('body').find(
    `[${TEMPLATE_SYNC_ELEMENT_IDENTIFIER}=${syncElementId}]`,
  );

  const elementIds = syncElements.map((_, el) => $(el).attr('id')).get();

  return elementIds;
};

const processUpdateDesign = (
  data: any,
  templateElement: any,
  templatePart: any,
  unlinkOnly: boolean,
  template: any,
) => {
  if (template && isDisplayTemplate(template.type)) {
    const { html, translations } = templateElement;
    const { css } = templateElement;
    let { css: partCss } = templateElement;

    if (templatePart) {
      partCss = templatePart.css;
    }

    const $ = cheerio.load(html);
    const design = $('body').find('#spm_body');

    let designCss = JSON.parse(css || '{}');

    if (!unlinkOnly) {
      design.attr(TEMPLATE_SYNC_ELEMENT_NAME, templatePart.name);

      const partCssObject = JSON.parse(partCss || '{}');
      Object.keys(partCssObject).forEach((key) => {
        if (key in designCss) {
          const regex = /([^{}]*#spm_(?:widget|column|row)_[a-zA-Z0-9]+[^{]*{[^}]*})/gm;
          const matches = [...designCss[key].matchAll(regex)];
          const capturedBlocks = matches.map((match) => match[0]);

          designCss[key] = `${partCssObject[key]} ${capturedBlocks.join(
            ' ',
          )}`;
        }
      });
      designCss = JSON.parse(JSON.stringify(designCss));
      // Reset specific css rules and apply new rules
      const templatePartData = JSON.parse(templatePart.data || '{}');
      $('body').html(resetSpecificDesignRules($('body').html() || '', getTemplateParentTypeByType(template.type)));
      if (templatePartData.specificCssRules) {
        $('body').html(applyNewSpecificDesignRules($('body').html() || '', templatePartData.specificCssRules));
      }
    } else {
      design.removeAttr(TEMPLATE_SYNC_ELEMENT_IDENTIFIER);
      design.removeAttr(TEMPLATE_SYNC_ELEMENT_NAME);
    }

    const newHtml = $('body').html();

    const newHash = calculatePartHash(
      newHtml || '',
      JSON.stringify(designCss),
      translations,
    );

    return {
      html: newHtml,
      css: JSON.stringify(designCss),
      translations,
      templateHash: newHash,
      data,
    };
  }
  const { html } = templateElement;
  let { css: partCss } = templateElement;

  if (templatePart) {
    partCss = templatePart.css;
    const { data: partData } = templatePart;
    const partDataObject = JSON.parse(partData || '{}');
    if (partDataObject[HIDE_MOBILE_ATTRIBUTE]) {
      // eslint-disable-next-line no-param-reassign
      data[HIDE_MOBILE_ATTRIBUTE] = partDataObject[HIDE_MOBILE_ATTRIBUTE];
    } else {
      // eslint-disable-next-line no-param-reassign
      delete data[HIDE_MOBILE_ATTRIBUTE];
    }
  }

  let newHtml = html;

  if (!unlinkOnly) {
    newHtml = html.replace(
      /data-spm-element-sync-name="([^"]+)"/g,
      `data-spm-element-sync-name="${templatePart.name}"`,
    );
  } else {
    newHtml = newHtml.replace(/data-spm-element-sync-name="([^"]+)"/g, '');
    newHtml = newHtml.replace(/data-spm-element-sync-id="([^"]+)"/g, '');
  }

  const newHash = calculatePartHash(newHtml, '', '');

  newHtml = newHtml.replace(
    /data-spm_element_hash="([^"]+)"/g,
    `data-spm_element_hash="${newHash}"`,
  );

  // Remove old mobile style
  newHtml = newHtml.replace(/data-mobilefontsizeincrease="([^"]+)"/g, '');
  newHtml = newHtml.replace(
    /data-spmtranslationid-data-mobilefontsizeincrease="([^"]+)"/g,
    '',
  );

  return {
    html: newHtml,
    css: partCss,
    translations: '',
    templateHash: newHash,
    data,
  };
};

const processUpdateElementCss = (
  templateElementInitCss: string,
  syncElementId: string,
  idInTemplateElementHtml: string,
  templateElementHtml: string,
  templatePart: any,
  templatePartIdMapping: Record<string, string>,
  extractSyncElementStylesOnly = false,
) => {
  let newInitCss = templateElementInitCss;
  let newIdMapping = templatePartIdMapping;
  // Update current element
  const updatedCssObject = updateCss(
    newInitCss,
    syncElementId,
    idInTemplateElementHtml,
    templateElementHtml,
    extractSyncElementStylesOnly,
  );

  newInitCss = JSON.stringify(updatedCssObject);

  // Template Part
  const { css: templatePartCss } = templatePart;

  if (extractSyncElementStylesOnly) {
    const $templatePartHtml = cheerio.load(templatePart.html);
    const templatePartSyncElement = $templatePartHtml('body').find(
      `[${TEMPLATE_SYNC_ELEMENT_IDENTIFIER}="${syncElementId}"]`,
    );
    if (
      templatePartSyncElement && templatePartIdMapping[templatePartSyncElement.attr('id') || '']
    ) {
      newIdMapping = {
        [templatePartSyncElement.attr('id') || '']: templatePartIdMapping[templatePartSyncElement.attr('id') || ''],
      };
    }
  }

  const newPartCss = getNewPartCss(
    newIdMapping,
    templatePartCss,
    extractSyncElementStylesOnly,
  );

  return { newInitCss, newPartCss };
};

const reorderColumn = ($: cheerio.CheerioAPI, column: cheerio.Cheerio<cheerio.Node>, direction: string) => {
  if (direction === ResponsiveDisplayStackEnum.RTL) {
    column.attr('dir', direction);
    column.find('.spm_droppable_widget').each((index, columnItem) => {
      $(columnItem).attr('dir', ResponsiveDisplayStackEnum.LTR);
    });
  } else {
    column.removeAttr('dir');
    column.find('.spm_droppable_widget').each((index, columnItem) => {
      $(columnItem).removeAttr('dir');
    });
  }

  column.find('tr').each((index, tr) => {
    const $tr = $(tr);
    const cells = $tr.children().toArray();

    if (!$tr.attr('data-id-order')) {
      const ids = cells.map((cell) => $(cell).attr('id')).join(',');
      const encoded = Buffer.from(ids).toString('base64');
      $tr.attr('data-id-order', encoded);
    }

    if (direction === ResponsiveDisplayStackEnum.RTL) {
      const reversed = [...cells].reverse();
      $tr.empty();
      reversed.forEach((cell) => $tr.append(cell));
    } else {
      const encodedIds = $tr.attr('data-id-order');
      if (encodedIds) {
        const decoded = Buffer.from(encodedIds, 'base64').toString();
        const idOrder = decoded.split(',');
        const cellMap: Record<string, cheerio.Element> = {};
        cells.forEach((cell) => {
          const id = $(cell).attr('id');
          if (id) cellMap[id] = cell;
        });
        $tr.empty();
        idOrder.forEach((id) => {
          const cell = cellMap[id];
          if (cell) {
            $tr.append(cell);
          }
        });
      }
    }
  });
};

const getDisplayedElements = (html: string): string[] => {
  // Elements are hidden by adding "spm_hide_on_builder" class
  const allElements = [
    'spm_product_list_element_button',
    'spm_product_list_element_description',
    'spm_product_list_element_price',
    'spm_product_list_element_price_strike',
    'spm_product_list_element_image',
    'spm_product_list_element_title',
    'spm_product_list_title',
  ];

  const displayedElements: string[] = [];

  if (html) {
    allElements.forEach((displayableElement) => {
      // We check if class exists or not
      const regExp = new RegExp(`"([^"]*\\b${displayableElement}\\b(?:\\s[^"]+)*)"`, 'is');
      const matches = html.match(regExp);

      if (matches) {
        const [, match] = matches;
        const classList = match.split(' ');

        if (!classList.includes('spm_hide_on_builder')) {
          displayedElements.push(displayableElement);
        }
      }
    });
  }

  return displayedElements;
};

const setDisplayedElements = (elements: string[], html: string) => {
  let element = html;

  // Elements are hidden by adding "spm_hide_on_builder" class
  const allElements = [
    'spm_product_list_element_button',
    'spm_product_list_element_description',
    'spm_product_list_element_price',
    'spm_product_list_element_price_strike',
    'spm_product_list_element_image',
    'spm_product_list_element_title',
    'spm_product_list_title',
  ];

  if (element) {
    allElements.forEach((displayableElement) => {
      // We check if class exists or not
      const regExp = new RegExp(`"([^"]*\\b${displayableElement}\\b(?:\\s[^"]+)*)"`, 'is');

      if (element) {
        const matches = element.match(regExp);

        if (matches) {
          const [, match] = matches;
          const classList = match.split(' ');
          let newClassList = match;

          if (elements.includes(displayableElement) && classList.includes('spm_hide_on_builder')) {
            // If we need to display the item, we need to remove the class
            newClassList = classList.filter((className) => className !== 'spm_hide_on_builder').join(' ');
          } else if (!elements.includes(displayableElement) && !classList.includes('spm_hide_on_builder')) {
            // If we need to hide the item, we need to add the class
            newClassList = [...classList, 'spm_hide_on_builder'].join(' ');
          }

          // We replace the match by the new class list (containing or not the hidden class)
          element = element.replace(`class="${match}"`, `class="${newClassList}"`);
        }
      }
    });
  }

  return element;
};

const processUpdateElement = (
  template: any,
  data: any,
  templateElement: any,
  templatePart: any,
  syncElementId: string,
  unlinkOnly: boolean,
) => {
  const {
    html: templateElementHtml,
    css: templateElementCss,
    translations: templateElementTranslations,
  } = templateElement;
  let templatePartData: Record<string, any> = {};
  if (templatePart && templatePart.data) {
    templatePartData = JSON.parse(templatePart.data || '{}');
  }

  const idsInTemplateElementHtml = findSyncElementIds(syncElementId, templateElementHtml);

  const $ = cheerio.load(templateElementHtml);

  let templateElementInitCss = JSON.stringify(JSON.parse(templateElementCss || '{}'));
  let templateElementInitTranslations = JSON.stringify(
    JSON.parse(templateElementTranslations || '{}'),
  );

  // Update sync elements if template part has been updated
  if (!unlinkOnly) {
    const toMergeTranslations = [];
    const toMergesCss = [];

    let doMergeTranslations = false;

    // eslint-disable-next-line no-restricted-syntax
    for (const idInTemplateElementHtml of idsInTemplateElementHtml) {
      // Template Part
      const { html: newHtml, translations: newTranslations } = templatePart;

      let templatePartHtml = newHtml;

      // Get the element to keep
      if (
        templatePartData[TEMPLATE_PART_ELEMENT_PART_TYPE] !== ElementPartTypeEnum.STYLES
        && templatePart.type === TemplateStructureEnum.SECTION
      ) {
        const $templateElementHtml = cheerio.load(templateElementHtml);
        const $templatePartHtml = cheerio.load(templatePartHtml);

        const keepChildElementsIdentifiers = getChildElementsIdentifiers(templateElementHtml);

        keepChildElementsIdentifiers.forEach((identifier) => {
          const $childElement = $templateElementHtml('body').find(
            `[${TEMPLATE_PART_ELEMENT_IDENTIFIER}="${identifier}"][${TEMPLATE_SYNC_ELEMENT_IDENTIFIER}][${TEMPLATE_PART_ELEMENT_PART_TYPE}]`,
          );
          if ($childElement.length > 0) {
            const $templatePartChildElement = $templatePartHtml('body').find(
              `[${TEMPLATE_PART_ELEMENT_IDENTIFIER}="${identifier}"][${TEMPLATE_SYNC_ELEMENT_IDENTIFIER}][${TEMPLATE_PART_ELEMENT_PART_TYPE}]`,
            );
            if ($templatePartChildElement.length > 0 && $childElement.html() !== '') {
              $childElement.attr('id', $templatePartChildElement.attr('id'));
              $templatePartChildElement.replaceWith(
                $templateElementHtml.html($childElement),
              );
              templatePartHtml = $templatePartHtml('body').html() || '';
            }
          }
        });
      }

      // Replace template part ids
      const {
        newHtml: updatedNewHtml,
        idMapping: templatePartIdMapping,
      } = replaceOldIdsWithNew(templatePartHtml);

      // Update css
      if (!templatePartData[TEMPLATE_PART_ELEMENT_PART_TYPE] || templatePartData[TEMPLATE_PART_ELEMENT_PART_TYPE] === ElementPartTypeEnum.STYLES) {
        const { newInitCss, newPartCss } = processUpdateElementCss(
          templateElementInitCss,
          syncElementId,
          idInTemplateElementHtml,
          templateElementHtml,
          templatePart,
          templatePartIdMapping,
          templatePartData[TEMPLATE_PART_ELEMENT_PART_TYPE] === ElementPartTypeEnum.STYLES,
        );

        templateElementInitCss = newInitCss;
        toMergesCss.push(newPartCss);

        const syncElement = $('body').find(`[id="${idInTemplateElementHtml}"]`);

        doMergeTranslations = getWidgetType(syncElement.attr('data-widgettype') || '') === WidgetTypeEnum.SMART_PRODUCT_LIST;

        if (
          templatePartData[TEMPLATE_PART_ELEMENT_PART_TYPE] === ElementPartTypeEnum.STYLES
          && getWidgetType(syncElement.attr('data-widgettype') || '') !== WidgetTypeEnum.SMART_PRODUCT_LIST
        ) {
          // Update sync element
          syncElement.removeClass(TEMPLATE_SYNC_ELEMENT_CLASS);
          syncElement.attr(TEMPLATE_SYNC_ELEMENT_IDENTIFIER, syncElementId);
          syncElement.attr(TEMPLATE_SYNC_ELEMENT_NAME, templatePart.name);
          syncElement.attr(
            TEMPLATE_PART_ELEMENT_PART_TYPE,
            ElementPartTypeEnum.STYLES,
          );
          // Update sync element id to match the id in idMapping
          const $templatePartHtml = cheerio.load(newHtml);
          const templatePartSyncElement = $templatePartHtml('body').find(
            `[${TEMPLATE_SYNC_ELEMENT_IDENTIFIER}="${syncElementId}"]`,
          );
          if (templatePartSyncElement && templatePartIdMapping[templatePartSyncElement.attr('id') || '']) {
            syncElement.attr(
              'id',
              templatePartIdMapping[templatePartSyncElement.attr('id') || ''],
            );
            // Apply class based styles
            const restrictedClasses = [
              TEMPLATE_SYNC_ELEMENT_CLASS,
              BUILDER_ELEMENT_ACTIVE_CLASS,
              BUILDER_ELEMENT_HOVER_CLASS,
            ];

            // Remove class first
            syncElement.attr('class', '');

            const templatePartSyncElementClasses = (
              templatePartSyncElement.attr('class') || ''
            ).split(/\s+/);
            templatePartSyncElementClasses.forEach((className) => {
              if (!syncElement.hasClass(className) && !restrictedClasses.includes(className)) {
                syncElement.addClass(className);
              }
            });

            // Manage responsive display stack (rtl, ltr)
            if (templatePart.type === TemplateStructureEnum.SECTION) {
              if (!templatePartSyncElementClasses.find((className) => className === 'spm_responsive')) {
                syncElement.removeClass('spm_responsive');
                const rows = syncElement.find('.row');
                rows.each((index, row) => {
                  $(row).removeClass('spm_responsive');
                  $(row).find('.spm_responsive').each((indexRow, rowItem) => {
                    $(rowItem).removeClass('spm_responsive');
                  });
                });
              }
              const column = templatePartSyncElement.find('.columns');
              if (column) {
                const direction = column.attr('dir') || ResponsiveDisplayStackEnum.LTR;
                syncElement.find('.columns').each((index, columnItem) => {
                  reorderColumn($, $(columnItem), direction);
                });
              }
            }

            // Apply additional styles
            if (templatePartData[TEMPLATE_PART_ELEMENT_SUBTYPE]) {
              const additionalConfig = additionalStylesConfig[getTemplateParentTypeByType(template.type)];
              if (additionalConfig && additionalConfig[templatePartData[TEMPLATE_PART_ELEMENT_SUBTYPE]]) {
                const { selector, defaultReplaceSelectorId, properties } = additionalConfig[templatePartData[TEMPLATE_PART_ELEMENT_SUBTYPE]];
                const sourceSelectedElement = $templatePartHtml('body').find(selector.replace('{ID}', defaultReplaceSelectorId));
                const targetSelectedElement = $('body').find(selector.replace('{ID}', `[id="${templatePartIdMapping[templatePartSyncElement.attr('id') || '']}"]`));
                const sourceSelectedElementClasses = (
                  sourceSelectedElement.attr('class') || ''
                ).split(/\s+/);
                if (sourceSelectedElement && targetSelectedElement) {
                  properties.forEach((property) => {
                    if (property.type === 'attribute' && sourceSelectedElement.attr(property.name)) {
                      targetSelectedElement.attr(property.name, sourceSelectedElement.attr(property.name) || '');
                    }
                    if (property.type === 'class') {
                      if (!property.operator && sourceSelectedElementClasses.find((className) => className === property.name)) {
                        targetSelectedElement.removeClass(property.name);
                        targetSelectedElement.addClass(property.name);
                      } else if (property.operator) {
                        const { operator } = property;
                        if (operator === 'hasValue') {
                          const filteredSourceSelectedElementClasses = sourceSelectedElementClasses.filter((className) => className.startsWith(property.name));
                          const targetSelectedElementClasses = (
                            targetSelectedElement.attr('class') || ''
                          ).split(/\s+/).filter((className) => className.startsWith(property.name));
                          targetSelectedElementClasses.forEach((className) => {
                            targetSelectedElement.removeClass(className);
                          });
                          filteredSourceSelectedElementClasses.forEach((className) => {
                            targetSelectedElement.addClass(className);
                          });
                        }
                      }
                    }
                  });
                }
              }
            }
          }
        }
      }
      //= ============================================

      // Update translations
      if (!templatePartData[TEMPLATE_PART_ELEMENT_PART_TYPE] || doMergeTranslations) {
        const { translationsObject: updatedTranslations, translationsToKeep } = updateTranslations(
          template,
          templateElementHtml,
          templateElementInitTranslations,
          syncElementId,
          idInTemplateElementHtml,
        );

        templateElementInitTranslations = JSON.stringify(updatedTranslations);

        const { newPartTranslations, newHtml: finalHtml } = getNewPartTranslations(newTranslations, updatedNewHtml);
        toMergeTranslations.push(newPartTranslations);

        // Update sync element html
        let syncElement = $('body').find(
          `[${TEMPLATE_SYNC_ELEMENT_IDENTIFIER}="${syncElementId}"][id="${idInTemplateElementHtml}"]`,
        );

        const $finalHtml = cheerio.load(finalHtml);

        const firstElementInBody = $finalHtml('body').children().first();

        firstElementInBody.attr(
          TEMPLATE_SYNC_ELEMENT_IDENTIFIER,
          syncElementId,
        );
        firstElementInBody.attr(
          TEMPLATE_SYNC_ELEMENT_NAME,
          templatePart.name,
        );
        firstElementInBody.removeClass(TEMPLATE_SYNC_ELEMENT_CLASS);
        if (templatePartData[TEMPLATE_PART_ELEMENT_PART_TYPE] !== ElementPartTypeEnum.STYLES) {
          firstElementInBody.addClass(TEMPLATE_SYNC_ELEMENT_CLASS);
        }

        const newId = firstElementInBody.attr('id');

        const smartlistAttributesToKeep = ['s-engine', 's-nb_weeks', 's-ids', 's-excluded_ids', 's-excluded_categories', 's-excluded_manufacturers'];
        const smartlistAttributesToKeepValue: Record<string, string> = {};
        let displayedElements: string[] = [];

        if (
          getWidgetType(syncElement.attr('data-widgettype') || '') === WidgetTypeEnum.SMART_PRODUCT_LIST
          && templatePartData[TEMPLATE_PART_ELEMENT_PART_TYPE] === ElementPartTypeEnum.STYLES
        ) {
          smartlistAttributesToKeep.forEach((attribute) => {
            const pattern = new RegExp(`${attribute}="([^"]*)"`, 'is');
            const find = (template.smartLists[syncElement.attr('id') || ''] || '').match(pattern);
            if (find) {
              // eslint-disable-next-line prefer-destructuring
              smartlistAttributesToKeepValue[attribute] = find[1];
            } else {
              smartlistAttributesToKeepValue[attribute] = '';
            }
          });

          displayedElements = getDisplayedElements(template.smartLists[syncElement.attr('id') || ''] || '');
        }

        const templatePartIdentifier = syncElement.attr(TEMPLATE_PART_ELEMENT_IDENTIFIER) || '';

        syncElement.replaceWith($finalHtml('body').html() || '');
        syncElement = $('body').find(
          `[${TEMPLATE_SYNC_ELEMENT_IDENTIFIER}="${syncElementId}"][id="${newId}"]`,
        );

        if (templatePartIdentifier) {
          syncElement.attr(TEMPLATE_PART_ELEMENT_IDENTIFIER, templatePartIdentifier);
        }

        if (
          getWidgetType(syncElement.attr('data-widgettype') || '') === WidgetTypeEnum.SMART_PRODUCT_LIST
          && templatePartData[TEMPLATE_PART_ELEMENT_PART_TYPE] === ElementPartTypeEnum.STYLES
        ) {
          Object.entries(smartlistAttributesToKeepValue).forEach(([attribute, value]) => {
            const attributeElement = syncElement.find(`[${attribute}]`);
            if (attributeElement) {
              attributeElement.attr(attribute, value);
            }
          });
          // Keep smart product list title
          const smartlistTitle = syncElement.find('.spm_product_list_title');
          if (smartlistTitle.length) {
            const titleHtml = smartlistTitle.html() || '';
            let match;
            const keyRegExp = /\{LANG_([a-zA-Z0-9]+)(?:\.[a-zA-Z0-9]+)?\}/g;

            // eslint-disable-next-line no-cond-assign
            while ((match = keyRegExp.exec(titleHtml)) !== null) {
              const newKey = match[1];
              // eslint-disable-next-line no-restricted-syntax
              for (const lang in newPartTranslations) {
                if (newPartTranslations[lang][newKey] && translationsToKeep[lang] && translationsToKeep[lang].title) {
                  newPartTranslations[lang][newKey] = translationsToKeep[lang].title;
                }
              }
            }
          }

          // Keep smart product list button
          const smartlistButtonSelector = isDisplayTemplate(template.type) ? 'a.spm_product_list_element_button' : '.spm_product_list_element_button > tbody > tr > th > a';
          const smartlistButton = syncElement.find(smartlistButtonSelector);
          if (smartlistButton.length) {
            const buttonHtml = smartlistButton.html() || '';
            let match;
            const keyRegExp = /\{LANG_([a-zA-Z0-9]+)(?:\.[a-zA-Z0-9]+)?\}/g;

            // eslint-disable-next-line no-cond-assign
            while ((match = keyRegExp.exec(buttonHtml)) !== null) {
              const newKey = match[1];
              // eslint-disable-next-line no-restricted-syntax
              for (const lang in newPartTranslations) {
                if (newPartTranslations[lang][newKey] && translationsToKeep[lang] && translationsToKeep[lang].button) {
                  newPartTranslations[lang][newKey] = translationsToKeep[lang].button;
                }
              }
            }
          }
        }
      }
    }

    if (!templatePartData[TEMPLATE_PART_ELEMENT_PART_TYPE] || doMergeTranslations) {
      // eslint-disable-next-line no-restricted-syntax
      for (const toMergeTranslation of toMergeTranslations) {
        const temp = mergeTranslations(
          JSON.parse(templateElementInitTranslations),
          toMergeTranslation,
        );
        templateElementInitTranslations = JSON.stringify(temp);
      }
    }

    if (!templatePartData[TEMPLATE_PART_ELEMENT_PART_TYPE] || templatePartData[TEMPLATE_PART_ELEMENT_PART_TYPE] === ElementPartTypeEnum.STYLES) {
      // eslint-disable-next-line no-restricted-syntax
      for (const toMergeCss of toMergesCss) {
        const temp = mergeAndConcatCss(JSON.parse(templateElementInitCss), toMergeCss);
        templateElementInitCss = JSON.stringify(temp);
      }
    }
  } else {
    // Unlink sync elements
    // eslint-disable-next-line no-restricted-syntax
    for (const idInTemplateElementHtml of idsInTemplateElementHtml) {
      const syncElement = $('body').find(`[id="${idInTemplateElementHtml}"]`);
      if (syncElement) {
        syncElement.removeAttr(TEMPLATE_SYNC_ELEMENT_IDENTIFIER);
        syncElement.removeAttr(TEMPLATE_SYNC_ELEMENT_NAME);
        syncElement.removeClass(TEMPLATE_SYNC_ELEMENT_CLASS);
        syncElement.removeAttr(TEMPLATE_PART_ELEMENT_PART_TYPE);
        syncElement.removeAttr(TEMPLATE_PART_ELEMENT_IDENTIFIER);
      }
    }
    templateElementInitCss = JSON.stringify(JSON.parse(templateElementInitCss));
    templateElementInitTranslations = JSON.stringify(JSON.parse(templateElementInitTranslations));
  }

  const returnHtml = $('body').html();

  const templateHash = calculatePartHash(
    returnHtml || '',
    templateElementInitCss,
    templateElementInitTranslations,
  );

  return {
    html: returnHtml,
    css: templateElementInitCss,
    translations: templateElementInitTranslations,
    templateHash,
    data,
  };
};

const updateTemplateElement = (
  template: any,
  templateElement: any,
  templatePart: any,
  elementType: string,
  syncElementId: string,
  unlinkOnly: boolean,
) => {
  const data = JSON.parse(templateElement.data || '{}');
  if (data[TEMPLATE_SYNC_ELEMENT_NAME] && templatePart) {
    data[TEMPLATE_SYNC_ELEMENT_NAME] = templatePart.name;
  }
  if (unlinkOnly) {
    if (
      [TemplateStructureEnum.SECTION, TemplateStructureEnum.DESIGN].includes(
        elementType as TemplateStructureEnum,
      )
    ) {
      delete data[TEMPLATE_SYNC_ELEMENT_IDENTIFIER];
      delete data[TEMPLATE_SYNC_ELEMENT_NAME];
      delete data[TEMPLATE_PART_ELEMENT_PART_TYPE];
    } else if (
      elementType === TemplateStructureEnum.WIDGET
      && data[TEMPLATE_SYNC_ELEMENT_IDs]
    ) {
      data[TEMPLATE_SYNC_ELEMENT_IDs] = data[
        TEMPLATE_SYNC_ELEMENT_IDs
      ].filter((syncId: string) => syncId !== syncElementId);
    }
  }
  if (elementType !== TemplateStructureEnum.DESIGN) {
    return processUpdateElement(
      template,
      data,
      templateElement,
      templatePart,
      syncElementId,
      unlinkOnly,
    );
  }
  return processUpdateDesign(
    data,
    templateElement,
    templatePart,
    unlinkOnly,
    template,
  );
};

// eslint-disable-next-line import/prefer-default-export
export const updateSyncElementsInOpenedTemplates = (currentTemplateId: number, syncElement: any, deleted = false, templatesId: number[] = []) => {
  const templatesInState = JSON.parse(JSON.stringify(TemplateEditorState.templates)) as Template[];

  let templatesToUpdate = templatesInState
    .filter((template: Template) => template.id !== currentTemplateId);
  if (templatesId.length > 0) {
    templatesToUpdate = templatesToUpdate.filter((template: Template) => templatesId.includes(template.id));
  }

  const syncElementData = JSON.parse(syncElement.data || '{}');
  const syncElementId = syncElementData[TEMPLATE_SYNC_ELEMENT_IDENTIFIER] || '';

  if (!syncElementId) return;

  // eslint-disable-next-line no-restricted-syntax
  for (const templateToUpdate of templatesToUpdate) {
    const sectionToUpdates = templateToUpdate.sections.filter((section) => {
      const data = JSON.parse(section.data || '{}');
      if (
        (data[TEMPLATE_SYNC_ELEMENT_IDENTIFIER] && data[TEMPLATE_SYNC_ELEMENT_IDENTIFIER] === syncElementId)
        || (data[TEMPLATE_SYNC_ELEMENT_IDs] && data[TEMPLATE_SYNC_ELEMENT_IDs].find((syncId: string) => syncId === syncElementId))
      ) {
        return true;
      }
      return false;
    });
    // eslint-disable-next-line no-restricted-syntax
    for (const sectionToUpdate of sectionToUpdates) {
      const updatedSection = updateTemplateElement(
        templateToUpdate,
        sectionToUpdate,
        deleted ? null : syncElement,
        syncElement.type || '',
        syncElementId,
        !!deleted,
      );
      updateOpenedTemplate(templateToUpdate.id, sectionToUpdate.id_template_elements, updatedSection);
    }
  }
};
